import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Layout from '../components/Layout';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField,
  IconButton, Switch, FormControlLabel, Tooltip, CircularProgress,
  Alert, Chip, Grid, MenuItem as MuiMenuItem, Select, InputLabel,
  FormControl, OutlinedInput, Checkbox, ListItemText, Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import { MENU_ICONS, MENU_ROLES, DEFAULT_MENU_ITEMS, getMenuIcon } from '../config/menuIcons';

const ICON_NAMES = Object.keys(MENU_ICONS);

export default function MenuItems() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'settings', 'menuItems'));
        if (snap.exists() && snap.data().items?.length > 0) {
          // Ensure defaults present on older items (defensive)
          const loaded = snap.data().items.map(it => ({
            roles: [], enabled: true, isSystem: false, icon: 'Link', ...it,
          }));
          // Sort by order to be safe
          loaded.sort((a, b) => (a.order || 0) - (b.order || 0));
          setItems(loaded);
        } else {
          setItems(DEFAULT_MENU_ITEMS);
        }
      } catch (e) { console.error(e); setItems(DEFAULT_MENU_ITEMS); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  function updateItem(idx, field, value) {
    setItems(arr => arr.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  }

  function addItem() {
    setItems(arr => [...arr, {
      key: `menu_${Date.now()}`,
      label: 'New Item',
      path: '',
      icon: 'Link',
      order: arr.length + 1,
      enabled: true,
      roles: [],
      isSystem: false,
    }]);
  }

  function removeItem(idx) {
    const item = items[idx];
    const prompt = item.isSystem
      ? `"${item.label}" is a SYSTEM item \u2014 deleting removes it from everyone's menu and it won't come back on its own. Delete anyway?`
      : `Remove "${item.label}"?`;
    if (!window.confirm(prompt)) return;
    setItems(arr => arr.filter((_, i) => i !== idx));
  }

  async function save() {
    setSaving(true);
    try {
      // Re-stamp order based on current array position
      const withOrder = items.map((it, i) => ({ ...it, order: i + 1 }));
      await setDoc(doc(db, 'settings', 'menuItems'), { items: withOrder });
      setItems(withOrder);
      // Clear Layout's cache so the new menu shows on next route change
      try {
        sessionStorage.removeItem('tbv_layout_menu_items');
      } catch {}
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  function resetToDefaults() {
    if (!window.confirm('Reset all menu items to defaults? This will discard any custom items you added.')) return;
    setItems(DEFAULT_MENU_ITEMS);
  }

  function onDragStart(idx) { dragIdx.current = idx; }
  function onDragOver(e, idx) { e.preventDefault(); dragOverIdx.current = idx; }
  function onDrop() {
    const from = dragIdx.current;
    const to = dragOverIdx.current;
    if (from === null || to === null || from === to) return;
    const updated = [...items];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setItems(updated);
    dragIdx.current = null;
    dragOverIdx.current = null;
  }

  if (loading) return (
    <Layout>
      <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
    </Layout>
  );

  return (
    <Layout>
      <Box sx={{ bgcolor: '#1E3A5F', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">🧭 Sidebar Menu Management</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            Manage items shown in the sidebar menu for each role. Changes are live immediately.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: 6 }}>
        {saved && <Alert severity="success" sx={{ mb: 3 }}>Menu items saved! Changes are live immediately.</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          Drag items to reorder. Each item is visible to the selected roles only. System items (🔒) can be deleted with an extra confirmation — disabling is usually the safer choice.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {items.map((item, idx) => {
            const IconComp = getMenuIcon(item.icon);
            return (
              <Card key={item.key} elevation={2} sx={{
                borderRadius: 3, opacity: item.enabled ? 1 : 0.65,
                border: '1px solid', borderColor: item.enabled ? 'primary.main' : 'divider',
              }}
                draggable onDragStart={() => onDragStart(idx)}
                onDragOver={e => onDragOver(e, idx)} onDrop={onDrop}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={2} alignItems="flex-start">

                    {/* Drag handle */}
                    <Grid sx={{ display: 'flex', alignItems: 'center', pt: '20px !important' }}>
                      <DragIndicatorIcon sx={{ color: 'text.disabled', cursor: 'grab' }} />
                    </Grid>

                    {/* Icon preview */}
                    <Grid sx={{ pt: '16px !important' }}>
                      <Box sx={{
                        width: 56, height: 56, borderRadius: 2,
                        bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid', borderColor: 'divider',
                      }}>
                        <IconComp sx={{ fontSize: 32, color: '#1E3A5F' }} />
                      </Box>
                    </Grid>

                    {/* Fields */}
                    <Grid size="grow">
                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth size="small" label="Label" value={item.label}
                                     onChange={e => updateItem(idx, 'label', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Icon</InputLabel>
                            <Select label="Icon" value={item.icon || 'Link'}
                                    onChange={e => updateItem(idx, 'icon', e.target.value)}>
                              {ICON_NAMES.map(name => {
                                const Ico = MENU_ICONS[name];
                                return (
                                  <MuiMenuItem key={name} value={name}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Ico fontSize="small" />
                                      <span>{name}</span>
                                    </Stack>
                                  </MuiMenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField fullWidth size="small" label="Path" value={item.path}
                                     placeholder="/page-name"
                                     onChange={e => updateItem(idx, 'path', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Visible to roles</InputLabel>
                            <Select multiple value={item.roles || []}
                                    onChange={e => updateItem(idx, 'roles', e.target.value)}
                                    input={<OutlinedInput label="Visible to roles" />}
                                    renderValue={selected => selected.length === 0
                                      ? <em style={{ color: '#888' }}>None — hidden from everyone</em>
                                      : selected.map(v => MENU_ROLES.find(r => r.value === v)?.label || v).join(', ')}>
                              {MENU_ROLES.map(r => (
                                <MuiMenuItem key={r.value} value={r.value}>
                                  <Checkbox checked={(item.roles || []).includes(r.value)} />
                                  <ListItemText primary={r.label} />
                                </MuiMenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <FormControlLabel
                            control={<Switch size="small" checked={item.enabled}
                                            onChange={e => updateItem(idx, 'enabled', e.target.checked)} />}
                            label={<Typography variant="caption" color={item.enabled ? 'success.main' : 'text.disabled'}>
                              {item.enabled ? 'Enabled' : 'Disabled (hidden)'}
                            </Typography>}
                            sx={{ m: 0 }} />
                        </Grid>
                      </Grid>

                      {/* Status chips */}
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
                        {item.isSystem && <Chip icon={<LockIcon sx={{ fontSize: 14 }} />} label="System" size="small" color="default" />}
                        {!item.enabled && <Chip label="Disabled" size="small" color="warning" />}
                        {!item.path && item.enabled && <Chip label="No path set" size="small" color="error" />}
                        {(!item.roles || item.roles.length === 0) && <Chip label="No roles — hidden" size="small" color="error" />}
                      </Box>
                    </Grid>

                    {/* Delete */}
                    <Grid sx={{ pt: '16px !important' }}>
                      <Tooltip title={item.isSystem ? 'Delete system item (extra confirmation)' : 'Remove item'}>
                        <span>
                          <IconButton size="small" color="error"
                                      onClick={() => removeItem(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={saving ? null : <SaveIcon />}
                  onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Menu'}
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addItem}>Add Item</Button>
          <Button variant="outlined" color="warning" onClick={resetToDefaults}>Reset to Defaults</Button>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin')}>Back</Button>
        </Box>
      </Container>
    </Layout>
  );
}
